import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, CircularProgress, Alert, Switch,
  Select, MenuItem, Tooltip, Snackbar,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { httpClient } from '../services/http-client';

interface AdminUser {
  id: string;
  email: string;
  display_name: string;
  plan: 'free' | 'paid';
  is_admin: boolean;
  is_super_admin: boolean;
  is_verified: boolean;
  created_at: string;
  /** The founding super-admin, whose roles cannot be changed by anyone. */
  protected: boolean;
}

/**
 * Platform administration: who is on which plan, and who holds which role.
 *
 * Two roles, deliberately distinct:
 *
 *   Admin        household operator — may connect banks and consume a Plaid
 *                Item from the shared pool.
 *   Super-admin  platform owner — may change everyone's plan and roles.
 *
 * The founding super-admin is immovable, enforced by a database trigger
 * rather than by this screen. Without that, the last super-admin could demote
 * themselves and lock the instance out of its own administration, recoverable
 * only by hand-editing production — the exact situation an admin UI exists to
 * remove. The controls are disabled here so the UI never offers an action
 * that would be refused.
 */
export const AdminUsersPage: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    httpClient
      .get<{ users: AdminUser[] }>('/admin/users')
      .then((r) => {
        const list = ((r as any)?.data?.users ?? (r as any)?.users ?? []) as AdminUser[];
        setUsers(Array.isArray(list) ? list : []);
        setError(null);
      })
      .catch((e: any) => setError(e?.message ?? 'Could not load users'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const update = async (u: AdminUser, patch: Partial<AdminUser>) => {
    const next = { ...u, ...patch };
    setSaving(u.id);
    try {
      await httpClient.put(`/admin/users/${u.id}/roles`, {
        plan: next.plan,
        is_admin: next.is_admin,
        is_super_admin: next.is_super_admin,
      });
      // Re-read rather than patching locally: the server may legitimately
      // refuse or adjust part of the change, and showing what was ASKED for
      // rather than what happened is how a UI starts lying.
      load();
      setToast(`Updated ${next.email}`);
    } catch (e: any) {
      setToast(e?.message ?? 'Update failed');
      load();
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>Users</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Plans and roles across the instance. <strong>Admin</strong> may connect
        banks; <strong>super-admin</strong> may change these settings.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell align="center">Admin</TableCell>
              <TableCell align="center">Super-admin</TableCell>
              <TableCell>Joined</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box>
                      <Typography variant="body2" fontWeight={650}>
                        {u.display_name || u.email.split('@')[0]}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {u.email}
                      </Typography>
                    </Box>
                    {u.protected && (
                      <Tooltip title="Founding super-admin. Roles are fixed at the database and cannot be changed by anyone.">
                        <LockIcon fontSize="small" color="disabled" />
                      </Tooltip>
                    )}
                    {!u.is_verified && (
                      <Chip label="unverified" size="small" variant="outlined" sx={{ height: 18 }} />
                    )}
                  </Stack>
                </TableCell>

                <TableCell>
                  <Select
                    size="small"
                    value={u.plan}
                    disabled={saving === u.id}
                    onChange={(e) => update(u, { plan: e.target.value as 'free' | 'paid' })}
                    sx={{ minWidth: 96 }}
                  >
                    <MenuItem value="free">Free</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                  </Select>
                </TableCell>

                <TableCell align="center">
                  <Switch
                    size="small"
                    checked={u.is_admin}
                    disabled={u.protected || saving === u.id}
                    onChange={(e) => update(u, { is_admin: e.target.checked })}
                  />
                </TableCell>

                <TableCell align="center">
                  <Switch
                    size="small"
                    checked={u.is_super_admin}
                    disabled={u.protected || saving === u.id}
                    onChange={(e) => update(u, {
                      is_super_admin: e.target.checked,
                      // Platform administration implies household operator.
                      // Granting the wider role without the narrower one
                      // produces someone who can change everyone's plan but
                      // cannot connect their own bank.
                      is_admin: e.target.checked ? true : u.is_admin,
                    })}
                  />
                </TableCell>

                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(u.created_at).toLocaleDateString()}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
      />
    </Box>
  );
};
